import { useForm, FormProvider, useWatch, Controller } from "react-hook-form";
import {
    FormControlLabel,
    Radio,
    RadioGroup,
    Autocomplete,
    TextField,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import type { SourceFile } from "../sources-types";
import { FormLabel, FTPInput, FTPSelect } from "../../../../../components-new";
import { formSubtitleClass } from "./addSourceStyles";
import { useSourceForm } from "./useSourceForm";
import { SourceFormFooter } from "./SourceFormFooter";
import { normalizeProjectsList, useStore } from "../../../../../helpers";

export type DataRepoKind = "es_index" | "db_connection";

type DataRepoFormValues = {
    name: string;
    projectId: string;
    surveyId: string;
    repoKind: DataRepoKind;
    es_index: string;
};

const DATA_REPO_TITLE = "Connect Data Repo";
const DATA_REPO_DEFAULT_NAME = "Data repo";

interface DataRepoFormProps {
    parentId: string | null;
    editNode?: SourceFile | null;
    onSuccess: () => void;
    onCancel: () => void;
}

export default function DataRepoForm({
    parentId,
    editNode,
    onSuccess,
    onCancel,
}: DataRepoFormProps) {
    const { isEdit, submitSource, renameNode } = useSourceForm(
        parentId,
        editNode ?? null,
        onSuccess
    );
    const rootStore = useStore();
    const { userStore, projectStore } = rootStore;

    const { data: projects = [] } = useQuery({
        queryKey: ["projects-list"],
        queryFn: () => userStore.getProjects(),
        select: normalizeProjectsList,
    });

    const projectOptions = projects.map(
        (p) => ({
            value: String(p.id),
            label: p.name ?? String(p.id),
        })
    );

    const methods = useForm<DataRepoFormValues>({
        defaultValues: {
            name: editNode?.name ?? "",
            projectId: "",
            surveyId: "",
            repoKind: "es_index",
            es_index: "",
        },
    });

    const selectedProjectId = useWatch({
        control: methods.control,
        name: "projectId",
        defaultValue: "",
    });
    const selectedSurveyId = useWatch({
        control: methods.control,
        name: "surveyId",
        defaultValue: "",
    });

    const projectSurveyIdDisplay = selectedSurveyId
        ? `p${selectedProjectId}_s${selectedSurveyId}`
        : "";

    const { data: fullProject, isLoading: fullProjectLoading } = useQuery({
        queryKey: ["full-project", selectedProjectId],
        queryFn: async () => {
            const response =
                await projectStore.getFullProject(selectedProjectId);
            return response.data as {
                surveys?: { id: number; name?: string }[];
            };
        },
        enabled: !!selectedProjectId,
        staleTime: 5 * 60 * 1000,
    });

    const surveys = fullProject?.surveys ?? [];
    const surveyOptions = surveys.map((s) => ({
        value: String(s.id),
        label: s.name ? `${s.name} - ${s.id}` : `Survey ${s.id}`,
    }));

    useEffect(() => {
        methods.setValue("surveyId", "");
    }, [selectedProjectId, methods]);

    useEffect(() => {
        methods.setValue("es_index", projectSurveyIdDisplay);
    }, [projectSurveyIdDisplay, methods]);

    const canSubmit = Boolean(selectedProjectId?.trim() && selectedSurveyId?.trim());

    const onSubmit = async (data: DataRepoFormValues) => {
        const name = data.name?.trim() || DATA_REPO_DEFAULT_NAME;
        if (isEdit) {
            await renameNode(name);
            return;
        }
        if (!data.projectId?.trim() || !data.surveyId?.trim()) return;
        await submitSource({
            type: data.repoKind,
            name,
            more: {
                ...(data.projectId ? { project_id: data.projectId } : {}),
                ...(data.surveyId ? { survey_id: data.surveyId } : {}),
                ...(data.es_index ? { es_index: data.es_index } : {}),
            },
        });
    };

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
                <h3 className={formSubtitleClass}>{DATA_REPO_TITLE}</h3>
                <FTPInput
                    name="name"
                    label="Name"
                    className="mb-3"
                    size="small"
                    placeholder="Data repo name"
                />
                <div className="mb-3">
                    <FormLabel className="mb-2 block">Project</FormLabel>
                    <Controller
                        name="projectId"
                        control={methods.control}
                        render={({ field }) => (
                            <Autocomplete
                                options={projectOptions}
                                getOptionLabel={(opt) =>
                                    opt && "label" in opt ? opt.label : ""
                                }
                                value={
                                    projectOptions.find(
                                        (o) => o.value === field.value
                                    ) ?? null
                                }
                                onChange={(_e, option) =>
                                    field.onChange(option?.value ?? "")
                                }
                                onBlur={field.onBlur}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Search or select project"
                                        size="small"
                                        variant="outlined"
                                    />
                                )}
                                renderOption={(props, option) => (
                                    <li {...props} key={option.value}>
                                        {option.label}
                                    </li>
                                )}
                            />
                        )}
                    />
                </div>
                <FTPSelect
                    name="surveyId"
                    label="Survey"
                    options={surveyOptions}
                    className="mb-3"
                    disabled={!selectedProjectId || fullProjectLoading}
                />
                {/* <FormLabel className="mb-1 block text-sm font-medium text-gray-700">Data repo type</FormLabel> */}
                {/* <Controller
          name="repoKind"
          control={methods.control}
          render={({ field }) => (
            <RadioGroup
              row
              className="mb-3"
              name={field.name}
              value={field.value}
              onChange={(e) => field.onChange(e.target.value as DataRepoKind)}
            >
              <FormControlLabel value="es_index" control={<Radio size="small" />} label="ES Index" />
              <FormControlLabel value="db_connection" control={<Radio size="small" />} label="DB Connection" />
            </RadioGroup>
          )}
        />
        <FTPInput name="connection_string" label="Connection string" className="mb-3" size="small" placeholder="Connection string" />
        <FTPInput name="index_name" label="Index name" className="mb-3" size="small" placeholder="Index name" />
        <Controller
          name="query_template"
          control={methods.control}
          render={({ field }) => (
            <div className="mb-3">
              <label className="mb-1 block text-sm font-medium text-gray-700">Query template</label>
              <textarea
                {...field}
                placeholder="Query template"
                className="min-h-[80px] w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-gray-500"
              />
            </div>
          )}
        /> */}
                {projectSurveyIdDisplay != null &&
                    String(projectSurveyIdDisplay).trim() !== "" && (
                        <div className="mb-3 rounded-lg px-3 py-2">
                            <span className="text-xs font-medium text-gray-500">
                                ES Index:{" "}
                                <span className="mt-0.5 text-sm font-medium text-gray-900">
                                    {projectSurveyIdDisplay}
                                </span>
                            </span>
                        </div>
                    )}
                <SourceFormFooter
                    onCancel={onCancel}
                    submitLabel={isEdit ? "Save" : "Add"}
                    disabled={!isEdit && !canSubmit}
                />
            </form>
        </FormProvider>
    );
}
